import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/hooks/use-toast';
import { MapPin, Camera, Phone, Lock, Globe, ArrowRight, ArrowLeft, Navigation, RotateCcw } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface CreateSOSModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  userLocation: { lat: number; lng: number } | null;
}

type SOSType = 'lost' | 'found' | 'emergency' | 'danger';
type LocationPrecision = 'precise' | 'street' | 'approximate';

const sosTypes = [
  {
    value: 'lost' as SOSType,
    label: 'Animale smarrito',
    icon: '🐕',
    description: 'Il mio animale è scomparso'
  },
  {
    value: 'found' as SOSType,
    label: 'Ho trovato un animale',
    icon: '🔍',
    description: 'Ho trovato un animale randagio'
  },
  {
    value: 'emergency' as SOSType,
    label: 'Emergenza/Animale ferito',
    icon: '🚨',
    description: 'Un animale ha bisogno di cure immediate'
  },
  {
    value: 'danger' as SOSType,
    label: 'Situazione di pericolo',
    icon: '⚠️',
    description: 'Situazione pericolosa per animali'
  }
];

export const CreateSOSModal = ({ open, onOpenChange, onSuccess, userLocation }: CreateSOSModalProps) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const marker = useRef<L.Marker | null>(null);
  
  // Form data
  const [type, setType] = useState<SOSType>('lost');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [animalSpecies, setAnimalSpecies] = useState('');
  const [animalBreed, setAnimalBreed] = useState('');
  const [animalName, setAnimalName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [location, setLocation] = useState(userLocation);
  const [locationPrecision, setLocationPrecision] = useState<LocationPrecision>('approximate');
  const [contactPhone, setContactPhone] = useState('');
  const [allowPublicSharing, setAllowPublicSharing] = useState(true);

  const resetForm = () => {
    setStep(1);
    setType('lost');
    setTitle('');
    setDescription('');
    setAnimalSpecies('');
    setAnimalBreed('');
    setAnimalName('');
    setImageUrl('');
    setLocation(userLocation);
    setLocationPrecision('approximate');
    setContactPhone('');
    setAllowPublicSharing(true);
    
    // Cleanup map
    if (map.current) {
      map.current.remove();
      map.current = null;
      marker.current = null;
    }
  };

  const getCurrentLocation = async () => {
    setGettingLocation(true);
    
    try {
      const { getCurrentLocationWithRetry } = await import('@/utils/geolocationUtils');
      const locationData = await getCurrentLocationWithRetry(2);
      
      if (locationData) {
        const newLocation = {
          lat: locationData.lat,
          lng: locationData.lng
        };
        setLocation(newLocation);
        updateMapLocation(newLocation);
        
        toast({
          title: "Posizione aggiornata",
          description: `Posizione rilevata con precisione ${Math.round(locationData.accuracy || 0)}m`
        });
      }
    } catch (error) {
      console.error('Error getting location:', error);
      toast({
        title: "Errore posizione",
        description: "Impossibile rilevare la posizione. Seleziona manualmente sulla mappa.",
        variant: "destructive"
      });
    } finally {
      setGettingLocation(false);
    }
  };

  const updateMapLocation = (newLocation: { lat: number; lng: number }) => {
    if (map.current && marker.current) {
      marker.current.setLatLng([newLocation.lat, newLocation.lng]);
      map.current.setView([newLocation.lat, newLocation.lng], 16);
    }
  };

  const initializeMap = () => {
    if (!mapContainer.current || map.current) return;

    const defaultLocation = location || { lat: 41.9028, lng: 12.4964 }; // Rome default
    
    map.current = L.map(mapContainer.current, {
      center: [defaultLocation.lat, defaultLocation.lng],
      zoom: location ? 16 : 10,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map.current);

    // Add draggable marker
    marker.current = L.marker([defaultLocation.lat, defaultLocation.lng], {
      draggable: true,
      icon: L.divIcon({
        html: `
          <div style="
            background-color: #dc2626;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            border: 3px solid white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            cursor: move;
          ">
            📍
          </div>
        `,
        className: 'custom-sos-marker',
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      })
    }).addTo(map.current);

    marker.current.bindPopup('Trascina per cambiare posizione');

    // Update location when marker is dragged
    marker.current.on('dragend', (e) => {
      const newLatLng = e.target.getLatLng();
      setLocation({
        lat: newLatLng.lat,
        lng: newLatLng.lng
      });
    });

    // Add click handler to map
    map.current.on('click', (e) => {
      const newLocation = {
        lat: e.latlng.lat,
        lng: e.latlng.lng
      };
      setLocation(newLocation);
      if (marker.current) {
        marker.current.setLatLng([newLocation.lat, newLocation.lng]);
      }
    });
  };

  useEffect(() => {
    if (step === 3 && open) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        initializeMap();
      }, 100);
    }
  }, [step, open]);

  useEffect(() => {
    setLocation(userLocation);
  }, [userLocation]);

  const handleSubmit = async () => {
    if (!user || !location) return;

    try {
      setLoading(true);

      const { error } = await supabase
        .from('sos_reports')
        .insert({
          user_id: user.id,
          type,
          title,
          description: description || null,
          animal_species: animalSpecies || null,
          animal_breed: animalBreed || null,
          animal_name: animalName || null,
          image_url: imageUrl || null,
          latitude: location.lat,
          longitude: location.lng,
          location_precision: locationPrecision,
          contact_phone: contactPhone || null,
          allow_public_sharing: allowPublicSharing
        });

      if (error) throw error;

      toast({
        title: "SOS pubblicato!",
        description: "Il tuo SOS è stato pubblicato. Le persone vicine verranno avvisate.",
      });

      resetForm();
      onSuccess();
    } catch (error) {
      console.error('Error creating SOS:', error);
      toast({
        title: "Errore",
        description: "Impossibile pubblicare il SOS. Riprova.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const canContinueStep1 = type !== undefined;
  const canContinueStep2 = title.trim() !== '' && animalSpecies.trim() !== '';
  const canSubmit = location !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            🚨 Invia SOS
            <Badge variant="outline">{step}/3</Badge>
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Scegli il tipo: ci aiuta a mostrare il tuo SOS alle persone giuste.
            </p>
            
            <div className="grid gap-3">
              {sosTypes.map((sosType) => (
                <Card
                  key={sosType.value}
                  className={`cursor-pointer transition-all hover:border-primary ${
                    type === sosType.value ? 'border-primary bg-primary/5' : ''
                  }`}
                  onClick={() => setType(sosType.value)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">{sosType.icon}</div>
                      <div className="flex-1">
                        <div className="font-medium">{sosType.label}</div>
                        <div className="text-sm text-muted-foreground">
                          {sosType.description}
                        </div>
                      </div>
                      {type === sosType.value && (
                        <div className="w-4 h-4 rounded-full bg-primary"></div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Button 
              onClick={() => setStep(2)} 
              disabled={!canContinueStep1}
              className="w-full"
            >
              Continua <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Aggiungi più dettagli possibile. Le foto aumentano le probabilità di ritrovamento.
            </p>

            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Titolo breve *</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="es. Cane smarrito zona Parco Centrale"
                  maxLength={100}
                />
              </div>

              <div>
                <Label htmlFor="species">Animale (specie) *</Label>
                <Input
                  id="species"
                  value={animalSpecies}
                  onChange={(e) => setAnimalSpecies(e.target.value)}
                  placeholder="es. Cane, Gatto, Coniglio..."
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="breed">Razza</Label>
                  <Input
                    id="breed"
                    value={animalBreed}
                    onChange={(e) => setAnimalBreed(e.target.value)}
                    placeholder="es. Labrador"
                  />
                </div>
                <div>
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={animalName}
                    onChange={(e) => setAnimalName(e.target.value)}
                    placeholder="Se conosciuto"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Descrizione</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Cosa è successo, segni particolari, comportamento..."
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="image">Foto (URL)</Label>
                <div className="flex gap-2">
                  <Input
                    id="image"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://esempio.com/foto.jpg"
                  />
                  <Button variant="outline" size="icon">
                    <Camera className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
              </Button>
              <Button 
                onClick={() => setStep(3)} 
                disabled={!canContinueStep2}
                className="flex-1"
              >
                Continua <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Puoi limitare la precisione della posizione se preferisci più privacy.
            </p>

            <div className="space-y-4">
              <div>
                <Label>Posizione SOS</Label>
                <div className="space-y-3 mt-2">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={getCurrentLocation}
                      disabled={gettingLocation}
                      className="flex-1"
                    >
                      <Navigation className="w-4 h-4 mr-2" />
                      {gettingLocation ? 'Rilevando...' : 'Usa posizione attuale'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (userLocation) {
                          setLocation(userLocation);
                          updateMapLocation(userLocation);
                        }
                      }}
                      disabled={!userLocation}
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div 
                    ref={mapContainer} 
                    className="w-full h-64 rounded-lg border border-border"
                  />
                  
                  <p className="text-xs text-muted-foreground">
                    Clicca sulla mappa o trascina il marker per selezionare la posizione esatta
                  </p>
                </div>
              </div>

              <div>
                <Label>Precisione posizione</Label>
                <RadioGroup 
                  value={locationPrecision} 
                  onValueChange={(value) => setLocationPrecision(value as LocationPrecision)}
                  className="mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="precise" id="precise" />
                    <Label htmlFor="precise">📍 Precisa (entro 10m)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="street" id="street" />
                    <Label htmlFor="street">🛣️ Via/zona (entro 100m)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="approximate" id="approximate" />
                    <Label htmlFor="approximate">🏘️ Area approssimativa (entro 1km)</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label htmlFor="phone">Telefono (opzionale)</Label>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder="+39 123 456 7890"
                    type="tel"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  <Label htmlFor="sharing">Condivisione pubblica</Label>
                </div>
                <Switch
                  id="sharing"
                  checked={allowPublicSharing}
                  onCheckedChange={setAllowPublicSharing}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Permette di condividere su social e link pubblico
              </p>

              {location && (
                <Card className="bg-muted/30">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4" />
                      <span>Posizione: {location.lat.toFixed(4)}, {location.lng.toFixed(4)}</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="space-y-3">
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-4">
                  <h4 className="font-medium mb-2">Anteprima SOS</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span>{sosTypes.find(t => t.value === type)?.icon}</span>
                      <span className="font-medium">{title}</span>
                    </div>
                    <div>🐾 {animalSpecies} {animalBreed && `(${animalBreed})`}</div>
                    {animalName && <div>📛 {animalName}</div>}
                    <div className="text-xs text-muted-foreground">
                      📍 Posizione {locationPrecision === 'precise' ? 'precisa' : 
                                   locationPrecision === 'street' ? 'per via' : 'approssimativa'}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(2)}>
                  <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={!canSubmit || loading}
                  className="flex-1 bg-destructive hover:bg-destructive/90"
                >
                  {loading ? 'Pubblicando...' : 'Pubblica SOS'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};